import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Grid, Heading, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isNonEmptyString} from 'ramda-adjunct';

import {
  useGetMazdaWarrantySubletsQuery,
  useGetServiceWorkItemIntegrationsMazdaAdditionalInformationQuery,
  usePatchServiceWorkItemIntegrationsMazdaAdditionalInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getMazdaCatalogOptions} from '../../../utils/getMazdaCatalogOptions';

interface EditLabourItemMazdaInformationProps extends TestIdProps {
  orderId: string;
  serviceCaseId: string;
  serviceJobId: string;
  serviceItemId: string;
  isFooterPinned: boolean;
  onClose: VoidFunction;
}

export function EditLabourItemMazdaInformation(props: EditLabourItemMazdaInformationProps) {
  const {
    currentData: mazdaAdditionalInformation,
    isError: isMazdaAdditionalInformationError,
    isLoading: isMazdaAdditionalInformationLoading,
    isFetching: isMazdaAdditionalInformationFetching,
  } = useGetServiceWorkItemIntegrationsMazdaAdditionalInformationQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      serviceJobId: props.serviceJobId,
      serviceItemId: props.serviceItemId,
    },
    {refetchOnMountOrArgChange: true}
  );
  const {data: warrantySublets} = useGetMazdaWarrantySubletsQuery();
  const [patchMazdaAdditionalInformation] =
    usePatchServiceWorkItemIntegrationsMazdaAdditionalInformationMutation();

  const handleSubmit: FormSubmitHandler<FormType> = async (data) =>
    await patchMazdaAdditionalInformation({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.orderId,
      serviceJobId: props.serviceJobId,
      serviceItemId: props.serviceItemId,
      body: {
        repairDescription: toNullableString(data.repairDescription),
        invoiceNumber: toNullableString(data.invoiceNumber),
        invoiceDate: toNullableString(data.invoiceDate),
        invoiceCompanyName: toNullableString(data.invoiceCompanyName),
      },
    })
      .unwrap()
      .catch(handleApiError);

  return (
    <DataStatus
      isLoading={isMazdaAdditionalInformationLoading || isMazdaAdditionalInformationFetching}
      isError={isMazdaAdditionalInformationError}
    >
      <VStack spacing={1}>
        <Heading size={4}>{i18n.t('entity.serviceOrder.labels.mazdaPortalIntegration')}</Heading>
        <Text size="xSmall" color="secondary">
          {i18n.t('entity.serviceOrder.labels.mazdaPortalIntegrationDescription')}
        </Text>
      </VStack>
      <Separator />
      <Form<FormType>
        onSubmit={handleSubmit}
        experimentalZodSchema={formSchema}
        defaultValues={{
          repairDescription: mazdaAdditionalInformation?.repairDescription,
          invoiceNumber: mazdaAdditionalInformation?.invoiceNumber,
          invoiceDate: mazdaAdditionalInformation?.invoiceDate,
          invoiceCompanyName: mazdaAdditionalInformation?.invoiceCompanyName,
        }}
      >
        {(control) => {
          const actionButtons = (
            <ButtonGroup align="right">
              <Button
                onClick={props.onClose}
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.saveChanges')}
                data-testid={suffixTestId('save', props)}
              />
            </ButtonGroup>
          );

          return (
            <VStack spacing={4}>
              <Grid columns={2} spacing={4}>
                <FormField
                  control={control}
                  name="repairDescription"
                  type="choice"
                  options={getMazdaCatalogOptions(warrantySublets?.sublets)}
                  label={i18n.t('entity.workItem.labels.repairDescription')}
                  placeholder={i18n.t('general.labels.select')}
                  menuInPortal
                  data-testid={suffixTestId('repairDescription', props)}
                />
                <FormField
                  control={control}
                  name="invoiceNumber"
                  type="text"
                  label={i18n.t('entity.workItem.labels.invoiceNumber')}
                  data-testid={suffixTestId('invoiceNumber', props)}
                />
                <FormField
                  control={control}
                  name="invoiceDate"
                  type="apiDate"
                  label={i18n.t('entity.workItem.labels.invoiceDate')}
                  data-testid={suffixTestId('invoiceDate', props)}
                />
                <FormField
                  control={control}
                  name="invoiceCompanyName"
                  type="text"
                  label={i18n.t('entity.customer.labels.companyName')}
                  data-testid={suffixTestId('invoiceCompanyName', props)}
                />
              </Grid>
              {/* DialogFooter is pinned to the dialog bottom; when the Clara section renders its own
                  pinned footer in the same tab, the buttons fall back inline to avoid overlap. */}
              {props.isFooterPinned ? <DialogFooter>{actionButtons}</DialogFooter> : actionButtons}
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}

const toNullableString = (value: string | Nullish) => (isNonEmptyString(value) ? value : null);

const formSchema = z.object({
  repairDescription: z.string().nullish(),
  invoiceNumber: z.string().nullish(),
  invoiceDate: z.string().nullish(),
  invoiceCompanyName: z.string().nullish(),
});

type FormType = z.infer<typeof formSchema>;
